import { memo, useEffect, useState } from 'react';

import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../atoms/ShadCnSelect';

export interface PaginationProps {
  canGetNextPage: boolean;
  canGetPrevPage: boolean;
  prevPage: () => void;
  nextPage: () => void;
  currentPage: number;
  noOfPages?: number;
  goToPage?: (value: number) => void;
  isLoadingFacts?: boolean;
  testid?: string;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  isNewSearch?: boolean;
  setIsNewSearch?: (value: boolean) => void;
}

const Pagination = ({
  canGetPrevPage,
  canGetNextPage,
  prevPage,
  nextPage,
  currentPage,
  noOfPages,
  goToPage,
  pageSize = 50,
  onPageSizeChange,
  isNewSearch,
  setIsNewSearch,
}: PaginationProps) => {
  const [displayPageCount, setDisplayPageCount] = useState(noOfPages);
  const [currentPageString, setCurrentPageString] = useState('1');

  useEffect(() => {
    if (noOfPages !== undefined) {
      setDisplayPageCount(noOfPages);
    }
  }, [noOfPages]);

  useEffect(() => {
    if (isNewSearch) {
      goToPage && goToPage(0);
      setCurrentPageString('1');
      setIsNewSearch?.(false);
    }
  }, [isNewSearch]);

  useEffect(() => {
    if (currentPage !== parseInt(currentPageString) - 1) {
      setCurrentPageString((currentPage + 1).toString());
    }
  }, [currentPage]);

  const handleChangePage = (value: string) => {
    setCurrentPageString(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (noOfPages === undefined) return;

    if (event.key === 'Enter') {
      const pageNumber = parseInt(currentPageString) - 1;

      if (pageNumber < 0 || pageNumber >= noOfPages) {
        goToPage && goToPage(currentPage);
        setCurrentPageString((currentPage + 1).toString());
        return;
      }

      goToPage && goToPage(pageNumber);
    }
  };

  return (
    <div className="flex items-center justify-between text-xs">
      <button
        className="mr-1 flex cursor-pointer items-center rounded-full bg-gray-200 p-1 shadow-sm hover:opacity-80 disabled:cursor-not-allowed"
        onClick={prevPage}
        disabled={!canGetPrevPage}
        data-testid="pagination-prev"
      >
        <FontAwesomeIcon
          icon={faChevronLeft}
          className="h-3 w-3 text-gray-700"
          style={{ color: canGetPrevPage ? 'gray' : 'white' }}
        />
      </button>

      <p className="mr-1.5 flex flex-row items-center justify-center text-center">
        <input
          value={currentPageString}
          className="mx-1 h-5 w-8 rounded border px-1 disabled:bg-gray-100 disabled:text-gray-400"
          onChange={(e) => handleChangePage(e.target.value)}
          onKeyDown={handleKeyDown}
          inputMode="numeric"
          min="1"
          type="number"
          disabled={noOfPages === 1}
          data-testid="pagination-input"
        />
        <span className="pt-0.5">of {displayPageCount ?? 0}</span>
      </p>
      <button
        className={`ml-1 flex cursor-pointer items-center rounded-full bg-gray-200 p-1 shadow-sm hover:opacity-80 disabled:cursor-not-allowed`}
        onClick={nextPage}
        disabled={!canGetNextPage}
        data-testid="pagination-next"
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className="h-3 w-3 text-gray-700"
          style={{ color: canGetNextPage ? 'gray' : 'white' }}
        />
      </button>

      {onPageSizeChange && (
        <div className="ml-4 flex items-center gap-2">
          <span className="text-gray-600">Show:</span>
          <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(parseInt(value))}>
            <SelectTrigger className="h-8 w-16 text-xs shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default memo(Pagination);
